import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from './Header';
import { apiService } from '../services/api';
import { EmergingAssetSummary, EmergingAssetTrialGroup, EmergingAssetReportGroup } from '../types';
import { Building2, FlaskConical, ExternalLink, FileText, Pencil, Loader2 } from 'lucide-react';

const EmergingAssetPage: React.FC = () => {
  const { assetId } = useParams();
  const [asset, setAsset] = useState<EmergingAssetSummary | null>(null);
  const [trialGroups, setTrialGroups] = useState<EmergingAssetTrialGroup[]>([]);
  const [reportGroups, setReportGroups] = useState<EmergingAssetReportGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrials, setExpandedTrials] = useState<Record<number, boolean>>({});
  const [canEditEmerging, setCanEditEmerging] = useState(false);
  const [editing, setEditing] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<{ id: number; company_name: string }[]>([]);
  const [editName, setEditName] = useState('');
  const [editOtherNames, setEditOtherNames] = useState('');
  const [editCompanyId, setEditCompanyId] = useState<string>('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadAssetBundle = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const numericAssetId = Number(assetId);
      const [assetData, trialData, reportsRes] = await Promise.all([
        apiService.fetchEmergingAsset(numericAssetId),
        apiService.fetchEmergingAssetTrials(numericAssetId),
        apiService.fetchEmergingAssetReports(numericAssetId).catch(() => ({ data: [] })),
      ]);
      setAsset(assetData);
      setTrialGroups(trialData?.data || []);
      setReportGroups(Array.isArray(reportsRes?.data) ? reportsRes.data : []);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    void loadAssetBundle();
  }, [loadAssetBundle]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await apiService.getCurrentUser();
        if (!cancelled) {
          setCanEditEmerging(Boolean(u?.can_edit_emerging || u?.is_superuser));
        }
      } catch {
        if (!cancelled) setCanEditEmerging(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canEditEmerging) return;
    let cancelled = false;
    (async () => {
      const list = await apiService.fetchCompanySelectOptions();
      if (!cancelled) setCompanyOptions(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [canEditEmerging]);

  const startEdit = () => {
    if (!asset) return;
    setEditName(asset.asset_name || '');
    setEditOtherNames(asset.other_names || '');
    setEditCompanyId(asset.company_id != null ? String(asset.company_id) : '');
    setSaveError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetId || !asset) return;
    const nameTrim = editName.trim();
    if (!nameTrim) {
      setSaveError('Asset name is required.');
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      const numericId = Number(assetId);
      const body: {
        asset_name: string;
        other_names: string | null;
        company_id: number | null;
      } = {
        asset_name: nameTrim,
        other_names: editOtherNames.trim() ? editOtherNames.trim() : null,
        company_id: editCompanyId === '' ? null : Number(editCompanyId),
      };
      const res = await apiService.patchEmergingAsset(numericId, body);
      if (!res?.success) {
        setSaveError(res?.error || 'Update failed');
        return;
      }
      const refreshed = await apiService.fetchEmergingAsset(numericId);
      if (refreshed) setAsset(refreshed);
      setEditing(false);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || err?.message || 'Update failed');
    } finally {
      setSaveLoading(false);
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const toggleExpanded = (trialId: number) => {
    setExpandedTrials((prev) => ({ ...prev, [trialId]: !prev[trialId] }));
  };

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />
      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : !asset ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
            <p className="text-slate-500">Asset not found.</p>
          </div>
        ) : (
          <>
            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
                {!editing ? (
                  <>
                    <div className="min-w-0 flex-1">
                      <h1 className="text-3xl font-extrabold text-slate-900">{asset.asset_name}</h1>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                        {asset.company_name && asset.company_id ? (
                          <Link
                            to={`/emerging-companies/${asset.company_id}`}
                            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
                          >
                            <Building2 className="w-4 h-4 text-indigo-500" />
                            {asset.company_name}
                          </Link>
                        ) : asset.company_name ? (
                          <a
                            href={`https://www.google.com/search?q=${encodeURIComponent(asset.company_name)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
                          >
                            <Building2 className="w-4 h-4 text-indigo-500" />
                            {asset.company_name}
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : null}
                        {asset.ticker && (
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono">{asset.ticker}</span>
                        )}
                        {asset.status && (
                          <span className="inline-flex items-center gap-2">
                            <FlaskConical className="w-4 h-4 text-emerald-500" />
                            {asset.status}
                          </span>
                        )}
                      </div>
                      {asset.other_names && (
                        <p className="mt-3 text-xs text-slate-500">Also known as: {asset.other_names}</p>
                      )}
                    </div>
                    {canEditEmerging && (
                      <button
                        type="button"
                        onClick={startEdit}
                        className="inline-flex items-center gap-2 shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit asset
                      </button>
                    )}
                  </>
                ) : (
                  <form onSubmit={saveEdit} className="w-full space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <h2 className="text-lg font-bold text-slate-900">Edit asset</h2>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={saveLoading}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saveLoading}
                          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {saveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                          Save
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        Asset name
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(ev) => setEditName(ev.target.value)}
                        className="w-full max-w-2xl rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        Also known as (other names)
                      </label>
                      <input
                        type="text"
                        value={editOtherNames}
                        onChange={(ev) => setEditOtherNames(ev.target.value)}
                        placeholder="Optional synonyms, comma-separated"
                        className="w-full max-w-2xl rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        Company
                      </label>
                      <select
                        value={editCompanyId}
                        onChange={(ev) => setEditCompanyId(ev.target.value)}
                        className="w-full max-w-2xl rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      >
                        <option value="">No company linked</option>
                        {companyOptions.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.company_name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Updates <code className="text-[10px] bg-slate-100 px-1 rounded">emerging_assets_list</code> for this
                        asset (name appears across linked trials).
                      </p>
                    </div>
                    {saveError && <p className="text-sm font-medium text-rose-600">{saveError}</p>}
                  </form>
                )}
              </div>
            </section>

            {reportGroups.length > 0 && (
              <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Emerging Reports for This Asset</h2>
                <p className="text-sm text-slate-500 mb-4">
                  This asset appears in the following indication reports. Open a report to see efficacy, safety, and trial context.
                </p>
                <ul className="space-y-2">
                  {reportGroups.map((r) => (
                    <li key={r.indication_code}>
                      {r.report_url ? (
                        <Link
                          to={r.report_url}
                          className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 w-full max-w-md"
                        >
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span>{r.indication_name}</span>
                          {r.report_count != null && r.report_count > 0 && (
                            <span className="text-xs text-slate-500">({r.report_count} entries)</span>
                          )}
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                          {r.indication_name}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Indications With Asset in Development</h2>
              {trialGroups.length === 0 ? (
                <p className="text-slate-500 text-sm">No trials found for this asset.</p>
              ) : (
                <div className="space-y-6">
                  {trialGroups.map((group) => (
                    <div key={group.indication_code} className="border border-slate-100 rounded-2xl p-5">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-lg font-semibold text-slate-900">{group.indication_name}</h3>
                        {group.indication_code !== 'unmapped' && group.trials[0]?.report_url && (
                          <Link
                            to={group.trials[0].report_url}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
                          >
                            <FileText className="w-4 h-4" />
                            View Emerging Report
                          </Link>
                        )}
                      </div>
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Trial Name</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">NCT</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Phase</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Trial Status</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Enrollment</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Study Start</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Primary Completion</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Study Completion</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Last Updated Post</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {group.trials.map((trial) => {
                              const expanded = expandedTrials[trial.id] ?? false;
                              return (
                                <React.Fragment key={`${group.indication_code}-${trial.id}`}>
                                  <tr className="hover:bg-slate-50">
                                    <td className="px-3 py-2 text-slate-700">
                                      <button
                                        type="button"
                                        onClick={() => toggleExpanded(trial.id)}
                                        className="text-left w-full"
                                      >
                                        <div className="font-semibold">{trial.trial_name || trial.full_title || '—'}</div>
                                        {trial.full_title && trial.trial_name && (
                                          <div className="text-xs text-slate-500 mt-1 max-w-[360px] truncate" title={trial.full_title}>
                                            {trial.full_title}
                                          </div>
                                        )}
                                        <div className="text-[11px] text-indigo-600 mt-1">
                                          {expanded ? 'Hide details' : 'Show details'}
                                        </div>
                                      </button>
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">{trial.nct_id || '—'}</td>
                                    <td className="px-3 py-2 text-slate-600">{trial.phases || '—'}</td>
                                    <td className="px-3 py-2 text-slate-600">{trial.trial_status || trial.overall_status || '—'}</td>
                                    <td className="px-3 py-2 text-slate-600">{trial.enrollment_size || '—'}</td>
                                    <td className="px-3 py-2 text-slate-600">{formatDate(trial.study_start_date || undefined)}</td>
                                    <td className="px-3 py-2 text-slate-600">{formatDate(trial.primary_completion_date)}</td>
                                    <td className="px-3 py-2 text-slate-600">{formatDate(trial.study_completion_date || undefined)}</td>
                                    <td className="px-3 py-2 text-slate-600">
                                      {formatDate(trial.last_updated_post_date || trial.last_update_submit_date || undefined)}
                                    </td>
                                  </tr>
                                  {expanded && (
                                    <tr className="bg-slate-50/70">
                                      <td colSpan={9} className="px-3 py-3 text-sm text-slate-600">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Full Title</div>
                                            <div className="mt-1">{trial.full_title || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Indication Details</div>
                                            <div className="mt-1">{trial.indication_details || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Primary Endpoints</div>
                                            <div className="mt-1">{trial.primary_endpoints || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Secondary Endpoints</div>
                                            <div className="mt-1">{trial.secondary_endpoints || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Sponsor</div>
                                            <div className="mt-1">{trial.sponsor || '—'}</div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default EmergingAssetPage;
